/**
 * The canonical set of AI-voice speed steps. `value` is the multiplier stored on the
 * voiceover asset (`ai_voice_speed`) and sent to the backend; `displayRate` is the
 * perceived rate shown to users (it deliberately differs from `value`). Both the speed
 * selector UI (AIVoiceSpeed) and the agent's edit_ai_voiceover tool source their options
 * from here so the steps stay in one place.
 */
export type AIVoiceSpeedName = 'slowest' | 'slow' | 'normal' | 'fast' | 'fastest';

export interface AIVoiceSpeedOption {
    name: AIVoiceSpeedName;
    label: string;
    displayRate: string;
    value: number;
}

export const AI_VOICE_SPEED_OPTIONS: readonly AIVoiceSpeedOption[] = [
    { name: 'slowest', label: 'Slowest speed', displayRate: '0.5x', value: 0.75 },
    { name: 'slow', label: 'Slow speed', displayRate: '0.75x', value: 0.85 },
    { name: 'normal', label: 'Normal speed', displayRate: '1.0x', value: 1 },
    { name: 'fast', label: 'Fast speed', displayRate: '1.25x', value: 1.1 },
    { name: 'fastest', label: 'Fastest speed', displayRate: '1.5x', value: 1.2 },
];

/** Pace multiplier meaning "leave scene durations as they are". */
const NORMAL_PACE_MULTIPLIER = 100;

/** The voice speed that neither slows nor speeds up the narration. */
const NORMAL_SPEED_VALUE = 1;

/**
 * The scene-duration pace that matches a given AI-voice speed.
 *
 * Voice speed is a rate — higher is faster — while `paceMultiplier` scales scene
 * duration, so higher is slower. They are inverses, which is why picking a faster
 * voice has to *lower* the pace multiplier to keep text-only scenes moving at the
 * same rate as the narrated ones.
 *
 * Keyed off `value`, not `displayRate`: `value` is what reaches the TTS provider as the
 * literal speed setting, so it is the rate the narration actually changes by. `displayRate`
 * is the label shown alongside it and deliberately differs, so pacing text scenes by it
 * would move them at a different rate than the voice.
 */
export function getPaceMultiplierForVoiceSpeed(speed: number): number {
    if (!speed || !Number.isFinite(speed) || speed <= 0) {
        return NORMAL_PACE_MULTIPLIER;
    }
    return Math.round(NORMAL_PACE_MULTIPLIER / speed);
}

/**
 * The selectable voice speed a given scene pace corresponds to.
 *
 * The inverse of {@link getPaceMultiplierForVoiceSpeed}, snapped to the steps the
 * selector can actually show. Videos carry pace values the retired Fast/Normal/Slow
 * dropdown wrote, which line up with no speed step exactly, so this picks the closest
 * one rather than leaving the selector with nothing to display.
 */
export function getVoiceSpeedForPaceMultiplier(paceMultiplier: number): number {
    if (!paceMultiplier || !Number.isFinite(paceMultiplier) || paceMultiplier <= 0) {
        return NORMAL_SPEED_VALUE;
    }
    const speed = NORMAL_PACE_MULTIPLIER / paceMultiplier;
    return AI_VOICE_SPEED_OPTIONS.reduce((closest, option) =>
        Math.abs(option.value - speed) < Math.abs(closest.value - speed) ? option : closest,
    ).value;
}
